import { Table, Button, Space, Popconfirm, Modal, Form, Input, Select, message, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { getRequest, postRequest, deleteRequest } from '../../services/requestService';
import { showErrorToast, showSuccessToast } from '../../services/notificationsService';
import './index.scss';

export default () => {
  const [partners, setPartners] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  
  useDocumentTitle('White Label Partners');

  const loadPartners = async () => {
    setIsLoading(true);
    try {
      console.log('Loading partners from /backoffice/whitelabel/list');
      const res = await getRequest('/backoffice/whitelabel/list');
      console.log('Partners response:', res);
      if (res?.data?.partners) {
        setPartners(res.data.partners);
      } else {
        setPartners([]);
      }
    } catch (error) {
      console.error('Error loading partners:', error);
      showErrorToast('Failed to load partners');
      setPartners([]);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadPartners();
  }, []);


  const handleDelete = async (id) => {
    try {
      await deleteRequest(`/backoffice/whitelabel/delete/${id}`);
      showSuccessToast('Partner deleted successfully');
      loadPartners();
    } catch (error) {
      console.error('Error deleting partner:', error);
      showErrorToast('Failed to delete partner');
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const showEditModal = (partner) => {
    setEditingPartner(partner);
    setIsEditModalVisible(true);
    editForm.setFieldsValue({
      partnerName: partner.partnerName,
      name: partner.name,
      partnerKey: partner.partnerKey,
      kind: partner.kind,
      logoUrl: partner.logoUrl,
      entityName: partner.entityName,
      referringUserName: partner.referringUserName,
      imgUrl: partner.imgUrl,
      bannerColor: partner.bannerColor,
      redirectUrl: partner.redirectUrl,
      videoUrl: partner.videoUrl,
      isActive: partner.isActive
    });
  };

  const handleEditModalCancel = () => {
    setIsEditModalVisible(false);
    setEditingPartner(null);
    editForm.resetFields();
  };

  const handleEditModalOk = async () => {
    try {
      const values = await editForm.validateFields();
      
      // Check if partner name already exists (excluding current partner)
      const existingPartner = partners.find(
        partner => partner._id !== editingPartner._id && 
        partner.partnerName?.toLowerCase() === values.partnerName?.toLowerCase()
      );
      
      if (existingPartner) {
        editForm.setFields([
          {
            name: 'partnerName',
            errors: ['Partner name already exists'],
          },
        ]);
        return;
      }

      const updatedPartner = {
        ...editingPartner,
        ...values
      };
      
      await postRequest('/backoffice/whitelabel/create', updatedPartner);
      showSuccessToast('Partner updated successfully');
      setIsEditModalVisible(false);
      setEditingPartner(null);
      editForm.resetFields();
      loadPartners();
    } catch (error) {
      console.error('Error updating partner:', error);
      if (error.errorFields) {
        // Form validation errors
        return;
      }
      showErrorToast('Failed to update partner');
    }
  };

  const handleDeleteFromEditModal = async () => {
    try {
      await deleteRequest(`/backoffice/whitelabel/delete/${editingPartner._id}`);
      showSuccessToast('Partner deleted successfully');
      setIsEditModalVisible(false);
      setEditingPartner(null);
      editForm.resetFields();
      loadPartners();
    } catch (error) {
      console.error('Error deleting partner:', error);
      showErrorToast('Failed to delete partner');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      // Check if partner name already exists
      const existingPartner = partners.find(
        partner => partner.partnerName?.toLowerCase() === values.partnerName?.toLowerCase()
      );
      
      if (existingPartner) {
        form.setFields([
          {
            name: 'partnerName',
            errors: ['Partner name already exists'],
          },
        ]);
        return;
      }

      const newPartner = {
        partnerName: values.partnerName,
        name: values.partnerName,
        kind: values.kind,
        logoUrl: '',
        entityName: '',
        referringUserName: '',
        imgUrl: '',
        bannerColor: '',
        redirectUrl: '',
        videoUrl: '',
        isActive: false
      };
      
      await postRequest('/backoffice/whitelabel/create', newPartner);
      showSuccessToast('Partner created successfully');
      setIsModalVisible(false);
      form.resetFields();
      loadPartners();
    } catch (error) {
      console.error('Error creating partner:', error);
      if (error.errorFields) {
        // Form validation errors
        return;
      }
      showErrorToast('Failed to create partner');
    }
  };


  const columns = [

    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Partner Key',
      dataIndex: 'partnerKey',
      key: 'partnerKey',
    },
    {
      title: 'Kind',
      dataIndex: 'kind',
      key: 'kind',
      render: (kind) => kind || 'title',
    },
    {
      title: 'Logo URL',
      dataIndex: 'logoUrl',
      key: 'logoUrl',
      render: (url) => url ? <a href={url} target="_blank" rel="noopener noreferrer">View</a> : '-',
    },
    {
      title: 'Entity Name',
      dataIndex: 'entityName',
      key: 'entityName',
    },
    {
      title: 'Referring User',
      dataIndex: 'referringUserName',
      key: 'referringUserName',
    },
    {
      title: 'Banner Color',
      dataIndex: 'bannerColor',
      key: 'bannerColor',
      render: (color) => {
        console.log(color)
        return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '20px', height: '20px', background: color, border: '1px solid #ccc' }}></div>
        </div>
      )
      },
    },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Space>
            <Button onClick={() => showEditModal(record)}>Edit</Button>
            <Button 
              onClick={() => window.open(`https://getzorba.com/partner/${record.partnerKey}`, '_blank')}
              type="default"
            >
              View Invite Page
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this partner?"
              onConfirm={() => handleDelete(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        )
      }
  ];


  return (
    <div className="white-label-page">
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={showModal}>
          Add Partner
        </Button>
      </div>
      
      <Table
        rowKey="_id"
        dataSource={partners}
        loading={isLoading}
        columns={columns}
        pagination={{
          pageSize: 10,
        }}
      />

      <Modal
        title="Add New Partner"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Create Partner"
        cancelText="Cancel"
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            kind: 'title'
          }}
        >
          <Form.Item
            label="Internal partner name"
            name="partnerName"
            rules={[
              { required: true, message: 'Please enter partner name' },
              { min: 2, message: 'Partner name must be at least 2 characters' },
              { max: 50, message: 'Partner name must be less than 50 characters' }
            ]}
          >
            <Input placeholder="Enter partner name" />
          </Form.Item>

          <Form.Item
            label="Kind"
            name="kind"
            rules={[{ required: true, message: 'Please select partner kind' }]}
          >
            <Select placeholder="Select partner kind">
              <Select.Option value="title">Title</Select.Option>
              <Select.Option value="lender">Lender</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit Partner"
        open={isEditModalVisible}
        onCancel={handleEditModalCancel}
        width={600}
        footer={[
          <Button key="cancel" onClick={handleEditModalCancel}>
            Cancel
          </Button>,
          <Popconfirm
            key="delete"
            title="Are you sure you want to delete this partner?"
            onConfirm={handleDeleteFromEditModal}
            okText="Yes"
            cancelText="No"
          >
            <Button danger>
              Delete Partner
            </Button>
          </Popconfirm>,
          <Button key="save" type="primary" onClick={handleEditModalOk}>
            Save Changes
          </Button>
        ]}
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          <Form.Item
            label="Partner Name"
            name="partnerName"
            rules={[
              { required: true, message: 'Please enter partner name' },
              { min: 2, message: 'Partner name must be at least 2 characters' },
              { max: 50, message: 'Partner name must be less than 50 characters' }
            ]}
          >
            <Input placeholder="Enter partner name" />
          </Form.Item>

          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: 'Please enter name' },
              { min: 2, message: 'Name must be at least 2 characters' },
              { max: 50, message: 'Name must be less than 50 characters' }
            ]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>

          <Form.Item
            label="Partner Key"
            name="partnerKey"
            rules={[
              { required: true, message: 'Please enter partner key' },
              { min: 2, message: 'Partner key must be at least 2 characters' },
              { max: 50, message: 'Partner key must be less than 50 characters' }
            ]}
          >
            <Input placeholder="Enter partner key" />
          </Form.Item>

          <Form.Item
            label="Kind"
            name="kind"
            rules={[{ required: true, message: 'Please select partner kind' }]}
          >
            <Select placeholder="Select partner kind">
              <Select.Option value="title">Title</Select.Option>
              <Select.Option value="lender">Lender</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Logo URL"
            name="logoUrl"
          >
            <Input placeholder="Enter logo URL" />
          </Form.Item>

          <Form.Item
            label="Entity Name"
            name="entityName"
          >
            <Input placeholder="Enter entity name" />
          </Form.Item>

          <Form.Item
            label="Referring User Name"
            name="referringUserName"
          >
            <Input placeholder="Enter referring user name" />
          </Form.Item>

          <Form.Item
            label="Image URL"
            name="imgUrl"
          >
            <Input placeholder="Enter image URL" />
          </Form.Item>

          <Form.Item
            label="Banner Color"
            name="bannerColor"
          >
            <Input placeholder="Enter banner color (e.g., #23bdb5 or linear-gradient(180deg, #23bdb5 0%, #1c9891 100%))" />
          </Form.Item>
          <Form.Item
            label="Video URL"
            name="videoUrl"
          >
            <Input placeholder="Enter video URL" />
          </Form.Item>

          {/* <Form.Item
            label="Active Status"
            name="isActive"
            valuePropName="checked"
          >
            <Select placeholder="Select active status">
              <Select.Option value={true}>Active</Select.Option>
              <Select.Option value={false}>Inactive</Select.Option>
            </Select>
          </Form.Item> */}
        </Form>
      </Modal>
    </div>
  );
};
